import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { Input, Modal, FeaturedIcon } from '@unified-checkout/design-system';
import { LinkSimple } from '@unified-checkout/design-system/icons/index';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import {
  PAYME_HANDLE_MAX_LENGTH,
  isValidPayMeHandle,
} from '@app/dashboard/views/pages/inner_layout/PayMe/utils/handleValidation';
import styles from './edit-handle-modal.module.scss';

export interface EditHandleModalProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (handleName: string) => void;
  currentHandle: string;
  isLoading?: boolean;
  apiError?: string | null;
}

export const EditHandleModal = ({
  open,
  onClose,
  onUpdate,
  currentHandle,
  isLoading = false,
  apiError = null,
}: EditHandleModalProps) => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;
  const [handleName, setHandleName] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  // Initialize handle name when modal opens
  useEffect(() => {
    if (open) {
      setHandleName(currentHandle);
      setValidationError(null);
    }
  }, [open, currentHandle]);

  const validateHandleName = (value: string): string | null => {
    const trimmed = value.trim().toLowerCase();

    if (!trimmed) {
      return t(
        'innerLayout.paymePage.editHandleModal.validation.required'
      );
    }

    if (trimmed.length > PAYME_HANDLE_MAX_LENGTH) {
      return t(
        'innerLayout.paymePage.editHandleModal.validation.maxLength'
      );
    }

    if (!isValidPayMeHandle(trimmed)) {
      return t(
        'innerLayout.paymePage.editHandleModal.validation.invalidPattern'
      );
    }

    if (trimmed === currentHandle.toLowerCase()) {
      return t(
        'innerLayout.paymePage.editHandleModal.validation.sameAsCurrent'
      );
    }

    return null;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();

    // Limit to max length
    if (value.length <= PAYME_HANDLE_MAX_LENGTH) {
      setHandleName(value);
    }

    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError(null);
    }
  };

  const handleSubmit = () => {
    const validationError = validateHandleName(handleName);

    if (validationError) {
      setValidationError(validationError);
      return;
    }

    const trimmedName = handleName.trim();
    onUpdate(trimmedName);

    // Reset form (notification and modal close will be handled by parent)
    // setHandleName('');
    setValidationError(null);
  };

  const handleClose = () => {
    setHandleName('');
    setValidationError(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onCloseHandler={handleClose}
      hasCloseIcon={true}
      width="32rem"
      state="default"
      headerIcon={<FeaturedIcon icon={LinkSimple} size="xl" color="gray" />}
      keepHeaderIconDefaultColors={true}
      isOverlayAboveModal={true}
      title={t('innerLayout.paymePage.editHandleModal.title')}
      btnsProps={[
        {
          text: isMobile
            ? t('innerLayout.paymePage.editHandleModal.updateButtonMobile')
            : t('innerLayout.paymePage.editHandleModal.updateButton'),
          variant: 'contained',
          color: 'primary',
          onClickHandler: handleSubmit,
          disabled:
            !handleName.trim() ||
            handleName.trim() === currentHandle.toLowerCase() ||
            isLoading,
          loading: isLoading,
        },
        {
          text: t('form.cancel'),
          variant: 'outlined',
          color: 'secondary',
          onClickHandler: handleClose,
          disabled: isLoading,
        },
      ]}
    >
      <div className={styles['edit-handle-modal']}>
        <div className={styles['edit-handle-modal__input-section']}>
          <Input
            id="handle-name-input"
            name="handle-name-input"
            type="text"
            value={handleName}
            onChange={handleInputChange}
            placeholder={t(
              'innerLayout.paymePage.editHandleModal.handlePlaceholder'
            )}
            label={t(
              'innerLayout.paymePage.editHandleModal.handleLabel'
            )}
            hintText={apiError ? undefined : t(
              'innerLayout.paymePage.editHandleModal.hintText'
            )}
            error={validationError || apiError || undefined}
            status={validationError || apiError ? 'error' : 'default'}
            autoFocus
            disabled={isLoading}
          />
        </div>
      </div>
    </Modal>
  );
};

